import { getChartColor } from 'src/Libs/system-charts/shared/config/chartColors'
import { Box, useTheme, useMediaQuery } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Area, Line, ResponsiveContainer, ReferenceLine, ComposedChart as RCComposedChart, Bar, ReferenceArea } from 'recharts'
import { CartesianGrid, XAxis, YAxisLeft, YAxisRight } from '../../shared/utils/rechartComps'
import { RechartData, CustomTrace, CUSTOM_UNITS } from '../../shared/utils/chartModel'
import { getUniqueIdentifierFromCustomTrace } from '../../../system-charts/shared/utils/getUniqueIdentifierFromCustomTrace'
import { traceConf } from '../../../system-charts/shared/config/traceConf'
import { applySignsToData } from '../../../system-data-handler/utils/chartFunctions'
import { getChartHeight } from '../../../system-charts/shared/utils/system-chart'
import { WeatherBreakthroughDot } from '.'
import { SharedChartProps } from '../../shared/types'
import { REFERENCE_AREA_STROKE_OPACITY } from '../../shared/utils/consts'

const ComposedChart: React.FC<SharedChartProps> = ({
  unit,
  minPrefix,
  secondUnit,
  xAxisTicks,
  xAxisFormat,
  xAxisInterval,
  rechartData,
  customTraces,
  showForecast,
  impossibleTraces,
  hiddenTraces,
  dstSwitchTs,
  onElementClick,
  height,
  legendHeight,
  headerHeight,
  view,
  chartEventHandlers,
  children,
  zoomStartIndex,
  zoomEndIndex,
  handleDoubleClick,
  isWeatherBreakthroughDotVisible,
  calculatedMinPrefix,
}) => {
  const { t } = useCustomTranslation()
  const [chartHeight, setChartHeight] = useState(100)
  const changedRechartData: RechartData = applySignsToData(rechartData, customTraces)
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const upMd = useMediaQuery(theme.breakpoints.up('md'))

  useEffect(() => {
    if (height) {
      setChartHeight(getChartHeight(height, legendHeight, headerHeight))
    }
  }, [height, legendHeight, headerHeight])

  const onClick = (payload: Record<string, unknown>): void => {
    if (onElementClick && payload.payload) {
      onElementClick(payload.payload as Record<string, unknown>)
    }
  }

  const types = customTraces?.reduce(
    (prev, curr) => {
      const currType = curr.type

      if (currType) {
        prev[currType].push(curr)
      }
      return prev
    },
    {
      bar: [] as CustomTrace[],
      line: [] as CustomTrace[],
      area: [] as CustomTrace[],
    }
  )

  const lines = useMemo(() => {
    const createLines = (customTrace: CustomTrace): React.ReactElement[] => {
      if (traceConf[customTrace.key]) {
        const yAxisId = CUSTOM_UNITS.includes(traceConf[customTrace.key].unit ?? '') ? 'right' : 'left'
        const key = getUniqueIdentifierFromCustomTrace(customTrace)
        const dot = customTrace.key === 'ForecastPower' && isWeatherBreakthroughDotVisible ? <WeatherBreakthroughDot /> : false

        const lineProps = {
          key,
          dataKey: customTrace.alternateKey ?? customTrace.key,
          yAxisId: yAxisId,
          stroke: customTrace.color ?? getChartColor(customTrace.key),
          // name="OOF" // legend and hover
          strokeWidth: customTrace.thickness || traceConf[customTrace.key].custom.thickness || 1,
          strokeDasharray: customTrace.dash === 'dash' ? '5 5' : customTrace.dash === 'dot' ? '2 2' : undefined,
          opacity: customTrace.opacity || traceConf[customTrace.key].custom.opacity || 1,
          hide:
            impossibleTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
            hiddenTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
            customTrace.hidden,
          dot,
          isAnimationActive: false,
          connectNulls: true,
        }

        const line = (
          <Line
            type="monotone"
            unit={traceConf[customTrace.key].unit ?? unit}
            activeDot={{ onClick: (e, payload) => onClick(payload as unknown as Record<string, unknown>) }}
            {...lineProps}
          />
        )
        const components = [line]

        if (traceConf[customTrace.key].hasForecast) {
          const lineProps2 = {
            ...lineProps,
            key: 'f-' + lineProps.key,
            dataKey: 'f-' + lineProps.dataKey,
            strokeDasharray: '5 5',
            opacity: lineProps.opacity * 0.5,
            hide: impossibleTraces.includes(customTrace.key) || hiddenTraces.includes(customTrace.key) || !showForecast || customTrace.hidden,
          }
          components.push(<Line type="monotone" unit={unit} {...lineProps2} />)
        }
        return components
      }
      return []
    }

    if (!types?.line) return []
    return types.line.flatMap(customTrace => createLines(customTrace))
  }, [customTraces, hiddenTraces, impossibleTraces, showForecast, unit, types?.line]) //eslint-disable-line

  const areas = useMemo(() => {
    const createAreas = (customTrace: CustomTrace): React.ReactElement[] => {
      if (traceConf[customTrace.key]) {
        const yAxisId = CUSTOM_UNITS.includes(traceConf[customTrace.key].unit ?? '') ? 'right' : 'left'
        const key = getUniqueIdentifierFromCustomTrace(customTrace)
        const dot = customTrace.key === 'ForecastPower' && isWeatherBreakthroughDotVisible ? <WeatherBreakthroughDot /> : false

        const areaProps = {
          key,
          dataKey: customTrace.alternateKey ?? customTrace.key,
          yAxisId: yAxisId,
          stroke: customTrace.color ?? getChartColor(customTrace.key),
          // name="OOF" // legend and hover
          strokeWidth: customTrace.thickness || traceConf[customTrace.key].custom.thickness || 1,
          strokeDasharray: customTrace.dash === 'dash' ? '5 5' : customTrace.dash === 'dot' ? '2 2' : undefined,
          opacity: customTrace.opacity || traceConf[customTrace.key].custom.opacity || 1,
          hide:
            impossibleTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
            hiddenTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
            customTrace.hidden,
          dot,
          isAnimationActive: false,
          connectNulls: true,
        }

        const area = (
          <Area
            fill={customTrace.color ?? getChartColor(customTrace.key)}
            unit={traceConf[customTrace.key].unit ?? unit}
            stackId={customTrace.stackId}
            activeDot={{ onClick: (e, payload) => onClick(payload as unknown as Record<string, unknown>) }}
            {...areaProps}
          />
        )
        const components = [area]

        if (traceConf[customTrace.key].hasForecast) {
          const areaProps2 = {
            ...areaProps,
            key: 'f-' + areaProps.key,
            dataKey: 'f-' + areaProps.dataKey,
            strokeDasharray: '5 5',
            opacity: areaProps.opacity * 0.5,
            hide: impossibleTraces.includes(customTrace.key) || hiddenTraces.includes(customTrace.key) || !showForecast || customTrace.hidden,
          }
          components.push(
            <Area
              fill={customTrace.color ?? getChartColor(customTrace.key)}
              unit={traceConf[customTrace.key].unit ?? unit}
              stackId={customTrace.stackId}
              {...areaProps2}
            />
          )
        }
        return components
      }
      return []
    }
    if (!types?.area) return []
    return types.area.flatMap(customTrace => createAreas(customTrace))
  }, [customTraces, hiddenTraces, impossibleTraces, showForecast, unit, types?.area]) //eslint-disable-line

  const bars = useMemo(() => {
    const bars: React.ReactElement[] = []
    if (!types?.bar) return bars

    for (const customTrace of types.bar) {
      if (traceConf[customTrace.key]) {
        const yAxisId = CUSTOM_UNITS.includes(traceConf[customTrace.key].unit ?? '') ? 'right' : 'left'

        const key = getUniqueIdentifierFromCustomTrace(customTrace)

        bars.push(
          <Bar
            key={key}
            dataKey={key!}
            yAxisId={yAxisId}
            fill={customTrace.color ?? getChartColor(customTrace.key)}
            isAnimationActive={false}
            hide={
              impossibleTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
              hiddenTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
              customTrace.hidden
            }
            stackId={customTrace.stackId}
            onClick={onElementClick}
            unit={traceConf[customTrace.key].unit ?? unit}
          />
        )

        if (traceConf[customTrace.key].hasForecast) {
          bars.push(
            <Bar
              key={'f-' + key}
              dataKey={'f-' + key!}
              yAxisId={yAxisId}
              fill={customTrace.color ?? getChartColor(customTrace.key)}
              isAnimationActive={false}
              hide={
                impossibleTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
                hiddenTraces.includes(customTrace.alternateKey ?? customTrace.key) ||
                !showForecast ||
                customTrace.hidden
              }
              opacity={0.5}
              stackId={customTrace.stackId}
              unit={traceConf[customTrace.key].unit ?? unit}
            />
          )
        }
      }
    }

    return bars
  }, [customTraces, hiddenTraces, impossibleTraces, showForecast, unit, types?.bar]) //eslint-disable-line

  const _CartesianGrid = useMemo(() => CartesianGrid({ isSmall: downSm }), [downSm])
  const _XAxis = useMemo(
    () =>
      XAxis({
        tickFormat: xAxisFormat,
        xAxisInterval,
        tick: { fill: theme.palette.text.primary },
        ticks: xAxisTicks,
        mirror: false,
        isSmall: downSm,
      }),
    [xAxisFormat, theme, xAxisTicks, downSm, xAxisInterval]
  )
  const _YAxisLeft = useMemo(
    () =>
      YAxisLeft({
        view: view,
        minPrefix: minPrefix,
        tick: { fill: theme.palette.text.primary },
        mirror: downSm,
        isSmall: downSm,
        exactPrefix: calculatedMinPrefix,
      }),
    [view, theme, minPrefix, downSm, calculatedMinPrefix]
  )
  const _YAxisRight = useMemo(
    () =>
      YAxisRight({
        tick: { fill: theme.palette.text.primary },
        unit: secondUnit,
        mirror: downSm,
        isSmall: downSm,
      }),
    [theme, secondUnit, downSm]
  )

  return (
    <Box sx={{ userSelect: 'none', width: '100%' }} onDoubleClick={handleDoubleClick}>
      <ResponsiveContainer minHeight={downMd ? 350 : undefined} height={chartHeight} width={'100%'}>
        <RCComposedChart
          data={changedRechartData.data}
          style={
            upMd
              ? undefined
              : {
                  userSelect: 'none',
                }
          }
          {...chartEventHandlers}
        >
          {zoomStartIndex && zoomEndIndex && (
            <ReferenceArea yAxisId={'right'} x1={zoomStartIndex} x2={zoomEndIndex} strokeOpacity={REFERENCE_AREA_STROKE_OPACITY} />
          )}
          {_CartesianGrid}
          {_XAxis}
          {children}
          {dstSwitchTs && (
            <ReferenceLine
              x={dstSwitchTs}
              xAxisId={0}
              yAxisId={'left'}
              label={t('systemDataHandler.dstSwitch') as string}
              stroke="red"
              strokeDasharray={'3 3'}
              alwaysShow={true}
            />
          )}
          {areas}
          {bars}
          {lines}
          {_YAxisLeft}
          {_YAxisRight}
        </RCComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}

export default ComposedChart
